import { useRouter } from 'next/router'
import Link from 'next/link'
import Image from 'next/image'
import { css, Theme } from '@emotion/react'
import { Box, Typography } from '@mui/material'

import { categories } from '~/constants/categories'

type Props = {
  url: string
  src: string
  title: string
  excerpt: string
  date: string
  category: string[]
}

const BlogListCard: React.FC<Props> = ({
  url,
  src,
  title,
  excerpt,
  date,
  category,
}) => {
  const router = useRouter()

  return (
    <Link href={`/blog/${url}`} passHref>
      <a>
        <Box css={styles.container}>
          <Box css={styles.imgContainer}>
            <Image
              src={src}
              layout='fill'
              objectFit='cover'
              css={styles.image}
            />
          </Box>
          <Box css={styles.contentContainer}>
            <Box css={styles.categoryWrapper}>
              {category.map((item, idx) => {
                const { name, color } = categories[`${item}`]
                return (
                  <div
                    css={styles.categoryContainer(color)}
                    onClick={() => router.push(`/blog/category/${item}`)}
                    key={idx}>
                    <Typography variant='h4' css={styles.category}>
                      {name}
                    </Typography>
                  </div>
                )
              })}
            </Box>
            <Typography variant='h2' component='h2' css={styles.date}>
              {date}
            </Typography>
            <Typography variant='h2' component='h2' css={styles.title}>
              {title}
            </Typography>
            <Typography
              variant='body1'
              component='p'
              color='primary'
              css={styles.excerpt}>
              {excerpt}
            </Typography>
          </Box>
        </Box>
      </a>
    </Link>
  )
}

const styles = {
  container: css`
    position: relative;
    overflow: hidden;
    height: 450px;
    width: 350px;
    will-change: transform;
    border-radius: 20px;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
    margin: 0 auto 30px 0;
    &:before {
      z-index: 10;
      background-color: rgba(0, 0, 0, 0);
      transition: background-color 0.5s ease;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 55%;
      content: '';
    }
    &:hover:before {
      background-color: rgba(255, 255, 255, 0.3);
    }
    &:hover img {
      transform: scale(1.1);
    }
  `,
  imgContainer: css`
    position: relative;
    height: 55%;
    width: 100%;
    overflow: hidden;
  `,
  image: css`
    transition: transform 0.5s ease-in-out;
    background: white;
  `,
  contentContainer: css`
    height: 45%;
    width: 100%;
    background-color: white;
    position: relative;
  `,
  categoryWrapper: css`
    display: flex;
    position: absolute;
    top: 10px;
    right: 10px;
    justify-content: center;
  `,
  categoryContainer: color => css`
    z-index: 120;
    border-radius: 30px;
    margin-left: 5px;
    background-color: ${color};
    position: relative;
    &:before {
      z-index: 150;
      content: '';
      background-color: rgba(0, 0, 0, 0);
      position: absolute;
      border-radius: 30px;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: background-color 0.3s ease-in-out;
    }
    &:hover:before {
      background-color: rgba(255, 255, 255, 0.5);
    }
  `,
  category: css`
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 0.8rem;
    font-weight: 600;
    color: white;
    padding: 0.3rem 1.4rem;
  `,
  date: css`
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    color: black;
    padding: 10px;
  `,
  title: css`
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 1rem;
    font-weight: 800;
    color: #2c2c2c;
    padding: 5px 30px;
    @media (min-width: 960px) {
      font-size: 1.1rem;
    }
  `,
  excerpt: css`
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 0.5rem;
    font-weight: 500;
    padding: 15px 30px;
    color: #353535;
    @media (min-width: 960px) {
      font-size: 0.8rem;
    }
  `,
}

export default BlogListCard
